import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TableRowAction, TableMassAction, TableIndication } from '@app/shared/models/table-action-type.model';
import { TableColumnsConfigHelper } from '@app/core/models/configuration/table-columns-config-helper';
import * as _ from 'lodash';
import { FileDownloadService } from '@app/core/services/filedownload.service';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ColumnDefinitionModel } from '@app/shared/models/column-definition.model';
import { Sort } from '@angular/material/sort';
import { PageEvent } from '@angular/material/paginator';
import { v4 as uuidv4 } from 'uuid';
import { ModalHelper } from '@app/shared/models/modal-helper.model';
import { ProgressComponent } from '@app/shared/components/progress/progress.component';
import { HttpCancelService } from '@app/core/services/http-cancel.service';
import { ModalComponent } from '@app/shared/components/modal/modal.component';

@Component({
    template: ''
  })

export abstract class ObjectListBaseComponent<TModel> implements OnInit, OnDestroy, OnChanges {

    unsubscribe$: Subject<boolean> = new Subject<boolean>();

    @Input() configKey: string;
    @Input() data: TModel[];
    @Input() highlights: any[] = [];
    @Input() tableRowsActions: TableRowAction[] = [];
    @Input() tableIndications: TableIndication[] = [];
    @Input() tableMassActions: TableMassAction[] = [];
    @Input() canDownload: boolean = false;
    @Input() showNewButton: boolean = false;
    @Input() pageSize: number = 20;
    @Input() pageIndex: number = 0;
    @Input() paginatorLength: number = 10;
    @Input() serverPagination: boolean = false;
    @Input() exportFileName: string;
    @Input() onSortChange = (sort: Sort) => { };
    @Input() onPageChange = (page: PageEvent) => { };
    @Input() onDataSourceChanged: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Input() onExport = () => { }
    @Input() onExportDataSourceAvailable: EventEmitter<any> = new EventEmitter<any>();
    @Input() onClearFilters = () => { }
    @Input() onFiltersCleared: EventEmitter<any> = new EventEmitter<any>();
    @Input() onExportExcelCaller: EventEmitter<void> = new EventEmitter<void>();
    @Input() showClearFilterButton: boolean = false;
    @Input() showFilter: boolean = true;

    @Output() onServerSideAllDownload: EventEmitter<void> = new EventEmitter<void>();

    selectedIds: string[] = [];
    isAllSelected: boolean = false;
    displayedColumnsLabel: ColumnDefinitionModel[] = [];
    innerData: TModel[] = [];
    progressDialog: MatDialogRef<ProgressComponent>;

    private massDownloadRequestSubject$ = new BehaviorSubject<string>(null);
    public massDownloadRequest$ = this.massDownloadRequestSubject$.asObservable();

    constructor(public dialog: MatDialog,
        protected httpCancelService: HttpCancelService,
        protected fileDownloadService: FileDownloadService) { }

    ngOnChanges(): void {
        this.innerData = this.data;
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next(true);
    }

    ngOnInit(): void {
        this.displayedColumnsLabel = new TableColumnsConfigHelper().getColumnsDefinition(this.configKey);
        this.massDownloadRequest$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((requestId: string) => {
                if (_.isNil(requestId)) {
                    if (!_.isNil(this.progressDialog)) {
                        this.progressDialog.close(false);
                    }
                    return;
                }

                const progressModal = new ModalHelper().getProgressConfig(requestId);
                this.progressDialog = this.dialog.open(ProgressComponent, progressModal);

                this.progressDialog.afterClosed()
                    .pipe(takeUntil(this.unsubscribe$))
                    .subscribe((isSuccess: boolean) => {
                        this.massDownloadRequestSubject$.next(null);
                        this.progressDialog = null;
                        if (!isSuccess) {
                            this.httpCancelService.cancelPendingRequests();
                        }
                    });
            })
    }

    onRowSelectionChange = (tableRowsSelection: string[]) => {
        this.selectedIds = tableRowsSelection;
    }

    onAllRowSelectionChange = (isChecked: boolean) => {
        this.isAllSelected = isChecked;
    }

    onMassDownload = () => {
        if (this.serverPagination === true && this.isAllSelected === true) {
            this.onServerSideAllDownload.emit();
            return;
        }

        if (this.selectedIds.length > 0) {
            let massDownloadRequest = uuidv4();
            this.massDownloadRequestSubject$.next(massDownloadRequest);
            this.getAttachments(this.selectedIds, massDownloadRequest)
                .pipe(takeUntil(this.unsubscribe$))
                .subscribe((blobData: Blob) => {
                    this.massDownloadRequestSubject$.next(null);
                    let localDate = new Date().toLocaleDateString();
                    this.fileDownloadService.downloadBlob(blobData, `${this.configKey}_${localDate}.zip`, "application/zip");
                }, _ => {
                    this.massDownloadRequestSubject$.next(null);
                    const modalErreurConfig = new ModalHelper().getAlertConfig(`Une erreur est survenue lors du téléchargement.`);
                    this.dialog.open(ModalComponent, modalErreurConfig);
                });
        }
    }

    abstract getAttachments(selectedIds, massDownloadRequest): Observable<Blob>;
}
